"use client";
import React, { useState, useEffect } from "react";
import "./Dashboard.css";
import { AirQualityService, SensorDataResponse } from "../../services/airQualityService";
import { getDevicesByRegionApi, getDeviceListApi } from "../../lib/api/device";
import { getAlertStatsApi, AlertLogStats } from "../../lib/api/alertLog";

interface DashboardViewProps {
  version: string;
}

interface RegionalData {
  avgH2s: number;
  avgNh3: number;
  avgTemperature: number;
  avgHumidity: number;
  compositeGrade: string;
  deviceCount: number;
}

interface RealTimeDeviceStatus {
  totalDevices: number;
  onlineDevices: number;
  offlineDevices: number;
  onlineRate: number;
}

export default function DashboardView({ version: _version }: DashboardViewProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeRegion, setActiveRegion] = useState<string | null>(null);
  const [activeDataType, setActiveDataType] = useState<string>("복합악취"); // 활성 데이터 타입 탭
  const [cityWideData, setCityWideData] = useState<RegionalData | null>(null);
  const [regionalData, setRegionalData] = useState<RegionalData | null>(null);
  const [sensorData, setSensorData] = useState<SensorDataResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [renderKey, setRenderKey] = useState(0); // 강제 리렌더링용
  const [realTimeStatus, setRealTimeStatus] = useState<RealTimeDeviceStatus>({
    totalDevices: 0,
    onlineDevices: 0,
    offlineDevices: 0,
    onlineRate: 0,
  });
  const [deviceCache, setDeviceCache] = useState<{[regionCode: string]: number[]}>({});
  const [allDeviceIds, setAllDeviceIds] = useState<number[]>([]);
  const [alertStats, setAlertStats] = useState<AlertLogStats>({ alarm: 0, notification: 0, processed: 0 });

  const airQualityService = AirQualityService.getInstance();

  // 지역 ID를 region_code로 매핑
  const getRegionCodeByRegionId = (regionId: string): string => {
    const regionCodeMap: { [key: string]: string } = {
      // 노안면 (region_code: '4617038000')
      noanmyeon: '4617038000',

      // 빛가람동 (region_code: '4617062000') 
      biccgaramdong: '4617062000',

      // 다도면 (region_code: '4617042000')
      dadomyeon: '4617042000',

      // 다른 지역들의 region_code (예시 - 실제 DB에서 확인 필요)
      nampyeongeup: '4617025000',
      sejimyeon: '4617040001',
      wanggokmyeon: '4617041000',
      bannammyeon: '4617031000',
      gongsanmyeon: '4617032000',
      donggangmyeon3604035: '4617035000',
      dasimyeon: '4617036000',
      munpyeongmyeon: '4617037000',
      geumcheonmyeon3604039: '4617039000',
      sanpomyeon: '4617040000',
      bonghwangmyeon: '4617044000',
      songwoldong: '4617051000',
      yeonggangdong: '4617052000',
      geumnamdong: '4617053000',
      seongbukdong3604055: '4617055000',
      yeongsandong: '4617056000',
      ichangdong: '4617057000',
    };

    return regionCodeMap[regionId] || regionId;
  };

  // API를 통한 지역별 기기 조회
  const getDeviceIdsByRegion = async (regionId: string): Promise<number[]> => {
    const regionCode = getRegionCodeByRegionId(regionId);
    
    // 캐시에서 먼저 확인
    if (deviceCache[regionCode]) {
      return deviceCache[regionCode];
    }
    
    try {
      const response = await getDevicesByRegionApi(regionCode, 0, 100);
      const deviceIds = response.content.map(device => device.id);
      
      // 캐시에 저장
      setDeviceCache(prev => ({ ...prev, [regionCode]: deviceIds }));
      
      return deviceIds;
    } catch (error) {
      console.error(`Failed to fetch devices for region ${regionCode}:`, error);
      return [];
    }
  };

  // API를 통한 전체 기기 조회
  const getAllDeviceIds = async (): Promise<number[]> => {
    if (allDeviceIds.length > 0) {
      return allDeviceIds;
    }
    
    try {
      const response = await getDeviceListApi(0, 100);
      const deviceIds = response.content.map(device => device.id);
      setAllDeviceIds(deviceIds);
      return deviceIds;
    } catch (error) {
      console.error('Failed to fetch all devices:', error);
      return [];
    }
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // activeDataType 변경 추적
  useEffect(() => {
    console.log("activeDataType changed to:", activeDataType);
  }, [activeDataType]);

  // 실시간 장치현황 로드 함수
  const loadRealTimeStatus = async () => {
    try {
      const status = await airQualityService.getRealTimeDeviceStatus();
      setRealTimeStatus(status);
    } catch (error) {
      console.error("Failed to load real-time device status:", error);
    }
  };

  // 실시간 경고현황 로드 함수
  const loadAlertStatus = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const stats = await getAlertStatsApi(today);
      setAlertStats(stats);
      console.log("Alert stats loaded:", stats);
    } catch (error) {
      console.error("Failed to load alert stats:", error);
      setAlertStats({ alarm: 0, notification: 0, processed: 0 });
    }
  };

  // 데이터 로드 함수
  const loadData = async () => {
    try {
      setLoading(true);

      // 실시간 장치현황 로드
      await loadRealTimeStatus();
      
      // 실시간 경고현황 로드
      await loadAlertStatus();

      // 나주시 전체 대기질 데이터 로드
      try {
        console.log("Loading city-wide data...");
        const allDeviceIdsResult = await getAllDeviceIds();
        console.log("All device IDs:", allDeviceIdsResult);
        
        // 전체 기기들의 첫 번째 기기로 대표 데이터 설정
        if (allDeviceIdsResult.length > 0) {
          const cityData = await airQualityService.getRegionalAverage(allDeviceIdsResult.slice(0, 5)); // 최대 5개 기기로 제한
          console.log("City data loaded:", cityData);
          setCityWideData(cityData);
          setRegionalData(cityData);
        } else {
          throw new Error("No devices found");
        }
      } catch (error) {
        console.error("Failed to load city-wide data:", error);
        const fallbackData = {
          avgH2s: 0,
          avgNh3: 0,
          avgTemperature: 0,
          avgHumidity: 0,
          compositeGrade: "오류",
          deviceCount: 0,
        };
        setCityWideData(fallbackData);
        setRegionalData(fallbackData);
      }

      // 활성 지역이 있으면 해당 지역의 대표 기기 데이터만 로드 (테이블/차트용)
      if (activeRegion) {
        const regionDeviceIds = await getDeviceIdsByRegion(activeRegion);
        if (regionDeviceIds.length > 0) {
          try {
            const latestData = await airQualityService.getDeviceTodayData(regionDeviceIds[0]);
            setSensorData(latestData || []);
          } catch (error) {
            console.error("Failed to load region sensor data:", error);
            setSensorData([]);
          }
        } else {
          setSensorData([]);
        }
      } else {
        // 지역이 선택되지 않았으면 전체 기기 중 첫 번째 기기 데이터
        const allDeviceIdsResult = await getAllDeviceIds();
        if (allDeviceIdsResult.length > 0) {
          try {
            const latestData = await airQualityService.getDeviceTodayData(allDeviceIdsResult[0]);
            setSensorData(latestData || []);
          } catch (error) {
            console.error("Failed to load default sensor data:", error);
            setSensorData([]);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load air quality data:", error);
    } finally {
      setLoading(false);
    }
  };

  // 초기 데이터 로드
  useEffect(() => {
    loadData();
  }, []);

  // 활성 지역 변경 시 해당 지역 센서 데이터만 다시 로드
  useEffect(() => {
    if (activeRegion) {
      loadRegionSensorData();
    } else {
      // 지역 선택 해제 시 전체 기기 데이터 로드
      loadDefaultSensorData();
    }
  }, [activeRegion]);

  // 지역별 센서 데이터만 로드하는 함수
  const loadRegionSensorData = async () => {
    try {
      if (!activeRegion) return;
      
      console.log("=== Loading region sensor data ===");
      console.log("Active region:", activeRegion);
      console.log("Region name:", getRegionName(activeRegion));
      
      // 지역의 region_code 가져오기
      const regionCode = getRegionCodeByRegionId(activeRegion);
      console.log("Region code:", regionCode);
      
      // 지역별 통계 데이터 로드 (우측 패널용)
      try {
        console.log("Loading region summary data for:", regionCode);
        console.log("Calling API: /api/sensor-data/regions/" + regionCode + "/summary");
        const regionSummary = await airQualityService.getRegionSensorSummary(regionCode);
        console.log("Region summary loaded:", regionSummary);
        console.log("Previous regional data:", regionalData);
        
        // 지역별 통계로 우측 패널 업데이트
        setRegionalData(regionSummary);
        console.log("Updated regional data to:", regionSummary);
        console.log("State after update - regionalData should be:", regionSummary);
        
        // 업데이트 후 강제 리렌더링
        const newRenderKey = Date.now();
        setRenderKey(newRenderKey);
        console.log("Forced re-render with key:", newRenderKey);
        console.log("UI should now show:", {
          deviceCount: regionSummary.deviceCount,
          avgH2s: regionSummary.avgH2s?.toFixed(3),
          avgNh3: regionSummary.avgNh3?.toFixed(3),
          compositeGrade: regionSummary.compositeGrade
        });
      } catch (error) {
        console.error("Failed to load region summary:", error);
        console.error("Error details:", error.response?.data || error.message);
        console.error("Full error object:", error);
        
        // 지역 데이터 로드 실패 시 기본값 설정
        setRegionalData({
          avgH2s: 0,
          avgNh3: 0,
          avgTemperature: 0,
          avgHumidity: 0,
          compositeGrade: "데이터없음",
          deviceCount: 0,
        });
        
        // 실패 후에도 강제 리렌더링
        setRenderKey(prev => prev + 1);
      }
      
      // 지역의 센서 데이터 테이블용
      const regionDeviceIds = await getDeviceIdsByRegion(activeRegion);
      console.log("Region device IDs:", regionDeviceIds);
      
      if (regionDeviceIds.length > 0) {
        console.log("Fetching latest sensor data for device:", regionDeviceIds[0]);
        try {
          const latestData = await airQualityService.getDeviceTodayData(regionDeviceIds[0]);
          console.log("Latest sensor data loaded:", latestData ? "1 record" : "no data");
          if (latestData) {
            console.log("Latest data:", latestData);
          }
          
          setSensorData(latestData || []);
        } catch (error) {
          console.error("Failed to fetch sensor data for device:", regionDeviceIds[0], error);
          setSensorData([]);
        }
      } else {
        console.log("No devices found for region:", activeRegion, "- setting empty data");
        setSensorData([]);
      }
    } catch (error) {
      console.error("Failed to load region sensor data:", error);
      console.error("Error details:", error.message);
      setSensorData([]);
    }
  };

  // 기본 센서 데이터 로드하는 함수
  const loadDefaultSensorData = async () => {
    try {
      const allDeviceIdsResult = await getAllDeviceIds();
      if (allDeviceIdsResult.length > 0) {
        try {
          const latestData = await airQualityService.getDeviceTodayData(allDeviceIdsResult[0]);
          setSensorData(latestData || []);
        } catch (error) {
          console.error("Failed to fetch latest data for device:", allDeviceIdsResult[0], error);
          setSensorData([]);
        }
      }
    } catch (error) {
      console.error("Failed to load default sensor data:", error);
      setSensorData([]);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 활성 데이터 타입에 따른 값 반환
  const getValueByDataType = (data: SensorDataResponse, dataType: string): number => {
    switch (dataType) {
      case "복합악취":
        // 복합악취는 외부 API에서 추후 제공 예정으로 현재는 0으로 표기
        return 0;
      case "황화수소":
        return data.h2s || 0;
      case "암모니아":
        return data.nh3 || 0;
      case "메탄가스":
        return data.ch4 || 0; // CH4를 메탄가스로 사용
      default:
        return data.h2s || 0;
    }
  };

  // 활성 데이터 타입에 따른 단위 반환
  const getUnitByDataType = (dataType: string): string => {
    switch (dataType) {
      case "복합악취":
        return "배수";
      case "황화수소":
        return "ppm";
      case "암모니아":
        return "ppm";
      case "메탄가스":
        return "ppm";
      default:
        return "ppm";
    }
  };

  // 차트 데이터 생성 - activeDataType이 변경될 때마다 다시 계산되도록 useMemo 사용 안 함
  const chartData = React.useMemo(() => {
    console.log("Generating chart data for activeDataType:", activeDataType);
    
    if (sensorData.length === 0) {
      return [{ time: "데이터\n없음", value: 0 }];
    }

    return sensorData
      .slice(0, 7)
      .reverse()
      .map((data) => {
        const date = new Date(data.measuredAt);
        const timeStr = `${date.getHours()}시\n${date.getMinutes().toString().padStart(2, "0")}`;
        const value = getValueByDataType(data, activeDataType);
        console.log(`Data point: ${data.measuredAt}, Type: ${activeDataType}, Value: ${value}`);
        return {
          time: timeStr,
          value,
        };
      });
  }, [sensorData, activeDataType]);

  // 지역명 변환 함수
  const getRegionName = (regionId: string): string => {
    const regionNames: { [key: string]: string } = {
      nampyeongeup: "남평읍",
      sejimyeon: "세지면",
      wanggokmyeon: "왕곡면",
      bannammyeon: "반남면",
      gongsanmyeon: "공산면",
      donggangmyeon3604035: "동강면",
      dasimyeon: "다시면",
      munpyeongmyeon: "문평면",
      noanmyeon: "노안면",
      geumcheonmyeon3604039: "금천면",
      sanpomyeon: "산포면",
      dadomyeon: "다도면",
      bonghwangmyeon: "봉황면",
      songwoldong: "송월동",
      yeonggangdong: "영강동",
      geumnamdong: "금남동",
      seongbukdong3604055: "성북동",
      yeongsandong: "영산동",
      ichangdong: "이창동",
      biccgaramdong: "빛가람동",
    };
    return regionNames[regionId] || regionId;
  };

  // 읍면동 클릭 핸들러
  const handleRegionClick = async (regionId: string) => {
    console.log("=== REGION CLICK HANDLER START ===");
    console.log("클릭된 읍면동:", regionId, "=>", getRegionName(regionId));
    console.log("Region code mapping:", getRegionCodeByRegionId(regionId));
    
    // 상태 업데이트
    setActiveRegion(regionId);
    
    // 즉시 데이터 로드 (useEffect 대기하지 않고)
    try {
      const regionCode = getRegionCodeByRegionId(regionId);
      console.log("Immediately calling region summary for:", regionCode);
      const regionSummary = await airQualityService.getRegionSensorSummary(regionCode);
      console.log("Immediate region summary result:", regionSummary);
      
      // 즉시 상태 업데이트
      setRegionalData(regionSummary);
      console.log("Regional data state updated immediately");
      
      // 강제 리렌더링 트리거
      setRenderKey(prev => prev + 1);
      console.log("Force re-render triggered");
    } catch (error) {
      console.error("Error in immediate region data load:", error);
    }
    
    console.log("=== REGION CLICK HANDLER END ===");
  };

  return (
    <div className="dashboard-wrapper">
      {/* 메인 3열 구성 */}
      <div className="dashboard-grid">
        {/* 1열: 왼쪽 - 3개의 원형 차트 */}
        <div className="dashboard-column">
          {/* 장치현황 */}
          <div className="panel">
            <div className="panel_in">
              <h3 className="panel-title">나주시 실시간 장치현황</h3>
              <div className="cd01">
                <div className="cd01_lt">
                  <div className="circular-chart-wrapper">
                    <svg className="circular-progress" width="128" height="128">
                      <circle className="bg-circle" cx="64" cy="64" r="56" />
                      <circle
                        className="progress-circle progress-orange"
                        cx="64"
                        cy="64"
                        r="56"
                        strokeDasharray={`${2 * Math.PI * 56 * (realTimeStatus.onlineRate / 100)} ${2 * Math.PI * 56}`}
                        transform="rotate(-90 64 64)"
                      />
                    </svg>
                    <div className="circular-chart-text">
                      <div className="percentage" style={{ color: "#ea4021" }}>
                        {realTimeStatus.onlineRate}%
                      </div>
                      <div className="label" style={{ color: "#ea4021" }}>
                        Online rate
                      </div>
                    </div>
                  </div>
                </div>
                <div className="status-list">
                  <div className="status-item">
                    <span className="status-icon tp01"></span>
                    <span>모든장치</span>
                    <span className="status-value gray">{realTimeStatus.totalDevices}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon tp02"></span>
                    <span>온라인</span>
                    <span className="status-value orange">{realTimeStatus.onlineDevices}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon tp03"></span>
                    <span>오프라인</span>
                    <span className="status-value gray">{realTimeStatus.offlineDevices}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 경고현황 */}
          <div className="panel">
            <div className="panel_in">
              <h3 className="panel-title">나주시 실시간 경고현황</h3>
              <div className="cd01">
                <div className="cd01_lt">
                  <div className="circular-chart-wrapper">
                    <svg className="circular-progress" width="128" height="128">
                      <circle className="bg-circle" cx="64" cy="64" r="56" />
                      <circle
                        className="progress-circle progress-cyan"
                        cx="64"
                        cy="64"
                        r="56"
                        strokeDasharray={`${2 * Math.PI * 56 * (alertStats.processed / Math.max(alertStats.alarm, 1))} ${2 * Math.PI * 56}`}
                        transform="rotate(-90 64 64)"
                      />
                    </svg>
                    <div className="circular-chart-text">
                      <div className="percentage" style={{ color: "#00fce3" }}>
                        {alertStats.alarm > 0 ? Math.round((alertStats.processed / alertStats.alarm) * 100) : 0}%
                      </div>
                      <div className="label" style={{ color: "#00fce3" }}>
                        Resolved rate
                      </div>
                    </div>
                  </div>
                </div>
                <div className="status-list">
                  <div className="status-item">
                    <span className="status-icon tp04"></span>
                    <span>경고발생</span>
                    <span className="status-value gray">{alertStats.alarm}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon tp02"></span>
                    <span>대기</span>
                    <span className="status-value turq">{alertStats.notification}</span>
                  </div>
                  <div className="status-item">
                    <span className="status-icon tp03"></span>
                    <span>처리</span>
                    <span className="status-value gray">{alertStats.processed}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 민원처리현황 */}
          <div className="panel">
            <h3 className="panel-title">나주시 실시간 민원처리현황</h3>
            <div className="cd01">
              <div className="cd01_lt">
                <div className="circular-chart-wrapper">
                  <svg className="circular-progress" width="128" height="128">
                    <circle className="bg-circle" cx="64" cy="64" r="56" />
                    <circle
                      className="progress-circle progress-blue"
                      cx="64"
                      cy="64"
                      r="56"
                      strokeDasharray={`${2 * Math.PI * 56 * 0.8} ${2 * Math.PI * 56}`}
                      transform="rotate(-90 64 64)"
                    />
                  </svg>
                  <div className="circular-chart-text">
                    <div className="percentage" style={{ color: "#3c8de7" }}>
                      80%
                    </div>
                    <div className="label" style={{ color: "#3c8de7" }}>
                      Online rate
                    </div>
                  </div>
                </div>
              </div>
              <div className="status-list">
                <div className="status-item">
                  <span className="status-icon tp05"></span>
                  <span>악취신고</span>
                  <span className="status-value gray">10</span>
                </div>
                <div className="status-item">
                  <span className="status-icon tp02"></span>
                  <span>대기</span>
                  <span className="status-value blue">7</span>
                </div>
                <div className="status-item">
                  <span className="status-icon tp03"></span>
                  <span>처리</span>
                  <span className="status-value gray">3</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2열: 가운데 - 지도만 */}
        <div className="dashboard-column" style={{ width: "40%" }}>
          <div className="panel map-panel">
            <div className="map-header">
              <div className="map-title-wrapper">
                <h2 className="map-title">나주시 종합</h2>
                <span className="map-status">{cityWideData?.compositeGrade || "로딩중..."}</span>
              </div>
              <span className="status-emoji">
                <img src="/img/ddico01.png" alt="status" />
              </span>
              <div className="map-metrics">
                <div className="map-metrics-in">
                  <span className="metric-badge">
                    <span className="metric-label tp01">복합악취</span>
                    <span className="metric-value">0</span>
                  </span>
                  <span className="metric-badge">
                    <span className="metric-label">황화수소</span>
                    <span className="metric-value">{cityWideData?.avgH2s?.toFixed(3) || "-"}</span>
                  </span>
                  <span className="metric-badge">
                    <span className="metric-label">암모니아</span>
                    <span className="metric-value">{cityWideData?.avgNh3?.toFixed(3) || "-"}</span>
                  </span>
                  <span className="metric-badge">
                    <span className="metric-label">온도</span>
                    <span className="metric-value">
                      {cityWideData?.avgTemperature?.toFixed(1) || "-"}°C
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div className="map-container">
              {/* SVG 지도 삽입 시작 */}
              <svg
                version="1.1"
                id="NajuMap"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                x="0px"
                y="0px"
                width="100%"
                height="100%"
                viewBox="-10 -10 240 190"
                xmlSpace="preserve"
                className="naju-svg-map"
                preserveAspectRatio="xMidYMid meet"
              >
                <g id="UMD">
                  <g id="sub-나주시">
                    {/* 각 읍면동 path 요소들 */}
                    <path
                      id="nampyeongeup"
                      className={activeRegion === "nampyeongeup" ? "active-region" : ""}
                      onClick={() => handleRegionClick("nampyeongeup")}
                      d="M214.9,25.9l2.1,4.2l-0.5,7.7l2.7,3.2l-4.1,8.7l-1.6,5.5l-3.8,3.3l-0.1,6.7l-5,2.8l-2.9,6.3l9.3,5.6
                        l5.5,8.7l-4.1,6l-1.9,5.6l-4,3.8l2.6,5.3l-1.8,5.7l-4.5-2.1l-4.2-9.8l4-5l-4.8-2.3l-4.3-5.7l-5.3,0.1l-1.5-5.3l2.4-3.5l-2.3-6.8
                        l-5.7,1.8l1.3-4.2l-2.6-11.8l-3.3-5l6.1-7.8l-8.9-4l1.8-4.9l11.8-3.8l10.1-9.5l6.3,3.1l7.6-5.1L214.9,25.9z"
                    />
                    <text x="195" y="65" className="region-label">
                      남평읍
                    </text>

                    <path
                      id="sejimyeon"
                      className={activeRegion === "sejimyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("sejimyeon")}
                      d="M125.9,103.5l2.3,6.7l-0.4,5.4l7.7,8l-0.1,5.3l3.2,5.2l-1.9,4.7l4,2l4.1,8.7l-1.7,2.7l-8.8,2.2l-3.2-1.2
                        l-6.5,5.3l-4.4,0.7l-2.8-1.9l-2.6-9.1l-2.5-2.5l2.4-6.4l-4.7-4.5l-2.9-7.4l4.9-0.5l0.3-8.5l3.2-3.9l-0.9-14l7.6-0.2L125.9,103.5z"
                    />
                    <text x="120" y="130" className="region-label">
                      세지면
                    </text>

                    <path
                      id="wanggokmyeon"
                      className={activeRegion === "wanggokmyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("wanggokmyeon")}
                      d="M114.8,100.6l0.9,14l-3.2,3.9l-0.3,8.5l-4.9,0.5l-2.1-4.3l-12.8-4.1l-4.5,0.9l-9.6-4l-4.3,6.1l-7.6-2.2
                        l-2.5-4.4l-9.1-4.9l-1.3-4.1l3.5-5.1l4.9,1l7.6,4.4l4.2-0.2v-5.4l-6.2-0.9l3.2-7.5l3,5.3l7,1.8l4.6-2l10.5,4.7l6.3-3.3l8.2-8.9
                        L114.8,100.6z"
                    />
                    <text x="85" y="115" className="region-label">
                      왕곡면
                    </text>

                    <path
                      id="bannammyeon"
                      className={activeRegion === "bannammyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("bannammyeon")}
                      d="M87.9,120l-3.8,11l4.3,1.9l3.8,6.9v7.8l3.4,8.1l-7.4,5.5l-3-4l-7.1-0.8l-6.4,3.4l-5.2,0.1l2.2-13
                        l-3.5-9.5l8.4-0.7l2-4.5l-3.7-6.9l2-3.2l4.3-6.1L87.9,120z"
                    />
                    <text x="75" y="145" className="region-label">
                      반남면
                    </text>

                    <path
                      id="gongsanmyeon"
                      className={activeRegion === "gongsanmyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("gongsanmyeon")}
                      d="M56.9,101.4l-3.5,5.1l1.3,4.1l9.1,4.9l2.5,4.4l7.6,2.2l-2,3.2l3.7,6.9l-2,4.5l-8.4,0.7l-5.6-3.9
                        l-14.8,1.9l-5.7,2.1l0.5-3.6l-5.2-16.6l-2.9-4.2l-4.5-1.3l0.1-6.1l4.7-7.8l6.2-0.3l3.3-8.1l2.6-2.1l4.8,5.7l5.7,2.4L56.9,101.4z"
                    />
                    <text x="50" y="120" className="region-label">
                      공산면
                    </text>

                    <path
                      id="donggangmyeon3604035"
                      className={activeRegion === "donggangmyeon3604035" ? "active-region" : ""}
                      onClick={() => handleRegionClick("donggangmyeon3604035")}
                      d="M31.9,97.9l-4.7,7.8l-0.1,6.1l4.5,1.3l2.9,4.2l5.2,16.6l-0.5,3.6l-4.8,4.1l0.2,5.7l3.4,1.3
                        l-0.5,6.9l-4.3,2.5l-1.3,11.6l-9.4,8.3l-3-12.3L1,141.8l-1-5.6l4-4.9l4.6,1.8l4.3,6.2l3.4-3.6l-7.8-15.8l0.1-5.7l6.8-15.6l3-5.4
                        l4.7-3.4l6.8,1.5L31.9,97.9z"
                    />
                    <text x="20" y="135" className="region-label">
                      동강면
                    </text>

                    <path
                      id="dasimyeon"
                      className={activeRegion === "dasimyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("dasimyeon")}
                      d="M96.4,40.1l7.9,2.2l-5.8,5.2l-3.3,6.1l0.1,13.1L92,73.3l0.6,5.5L80.5,95l-10-2.3l-3.2,7.5l6.2,0.9v5.4
                        l-4.2,0.2l-7.6-4.4l-4.9-1l-2.4-5.9L48.7,93l-4.8-5.7l2.8-5.4l1.4-9.8L41,70.7l4.7-7.8l-0.6-4.5l9.4,1.8l8,6.4l3.8-3.8l1.5-9
                        l2.4-1.6l-1.8-6.7l2.4-7.8l3.2-4.2l5.7,3.5l2.7,5.4l10.8-0.3L96.4,40.1z"
                    />
                    <text x="70" y="70" className="region-label">
                      다시면
                    </text>

                    <path
                      id="munpyeongmyeon"
                      className={activeRegion === "munpyeongmyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("munpyeongmyeon")}
                      d="M87.6,6.4l1.5,10.5l2.4,7.7l4.6,6.5l0.3,8.8l-3.2,2l-10.8,0.3l-2.7-5.4L74,33.3l-3.2,4.2l-2.4,7.8
                        l1.8,6.7l-2.4,1.6l-1.5,9l-3.8,3.8l-8-6.4l-9.4-1.8l-3.6-11.3l0.8-7.2l1.8-5.1l2.6-16.1l-1.2-6.7l6.3-0.9l2.1,4.5l8.4,4.2l3.4-3.6
                        l10.2-2.6l-0.3-5.5l4.1-2.8l0.7-5.3l7.9,2.1L87.6,6.4z"
                    />
                    <text x="65" y="35" className="region-label">
                      문평면
                    </text>

                    <path
                      id="noanmyeon"
                      className={activeRegion === "noanmyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("noanmyeon")}
                      d="M131.1,7.7l5.6,8.6l4.3,0.5l2.7,11l2.3,3.9l-9.9,2.6l2,4.8l-6.4,8.4l-3.1,0.5l-0.3-10.3l-3.9-3.1l-8.7,2.3
                        l-6.5,4.3l-4.9,1.2l-7.9-2.2l-0.4-9l-4.6-6.5L89,17L87.6,6.4l5.9-0.1L98.4,2l3.3,2.7l6.9-3.3l2.3,2.8l12.3,1.2L131.1,7.7z"
                    />
                    <text x="115" y="25" className="region-label">
                      노안면
                    </text>

                    <path
                      id="geumcheonmyeon3604039"
                      className={activeRegion === "geumcheonmyeon3604039" ? "active-region" : ""}
                      onClick={() => handleRegionClick("geumcheonmyeon3604039")}
                      d="M150.3,43.6l4.2,14.1l-4.1,0.8l-3.1,10l6.6,8.2l4.3-1.3l1.3,6.9l-4.9,4.5l-6.8,2.5l-2.9,7.6
                        l-4.7-3.4V81.6l-8.7-2l-3.3-8.2l-0.8-2.5l0.5-9l-2.6-7.4l3.5-4.6l3.1-0.5l6.4-8.4L150.3,43.6z"
                    />
                    <text x="140" y="65" className="region-label">
                      금천면
                    </text>

                    <path
                      id="sanpomyeon"
                      className={activeRegion === "sanpomyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("sanpomyeon")}
                      d="M173.5,43.7l8.9,4l-6.1,7.8l3.3,5l2.6,11.8l-1.3,4.2l-7-0.7l-4.8,3.1l0.8,5.5l-6.1,2.1l-4.4-4.2l-1.3-6.9
                        l9,1.5l-1.9-13.8l-5.2-4.6l-5.6-0.7l-4.2-14.1l9.6-5.7l4.8,6.2L173.5,43.7z"
                    />
                    <text x="165" y="60" className="region-label">
                      산포면
                    </text>

                    <path
                      id="dadomyeon"
                      className={activeRegion === "dadomyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("dadomyeon")}
                      d="M207.2,115l-6.9,9l3.7,9.2l-0.5,13.1l2.2,3.2l-3.5,9.1l-11.1,2.8l-7.8,4l-7.1,6.8l-7-2.4l0.7-5.7l-5.8-2.8
                        l-1.5-8.5l-3.8-5.3l0.4-12.4l2-4.1l9.2-5.9l0.5-11l5-3.9l1.9-4.5l-6.4-3.5l-1.7-6l3.4-3.2l-3.2-8.6l-0.8-5.5l4.8-3.1l7,0.7
                        l5.7-1.8l2.3,6.8l-2.4,3.5l1.5,5.3l5.3-0.1l4.3,5.7l4.8,2.3l-4,5l4.2,9.8L207.2,115z"
                    />
                    <text x="185" y="130" className="region-label">
                      다도면
                    </text>

                    <path
                      id="bonghwangmyeon"
                      className={activeRegion === "bonghwangmyeon" ? "active-region" : ""}
                      onClick={() => handleRegionClick("bonghwangmyeon")}
                      d="M159.5,82.3l4.4,4.2l6.1-2.1l3.2,8.6l-3.4,3.2l1.7,6l6.4,3.5l-1.9,4.5l-5,3.9l-0.5,11l-9.2,5.9
                        l-2,4.1l-0.4,12.4l3.8,5.3l1.5,8.5l5.8,2.8l-0.7,5.7l-5.1,4.1l-4.9-6.7l-4.7-0.8l-0.9-4l-10.4-10.3l1.7-2.7l-4.1-8.7l-4-2l1.9-4.7
                        l-3.2-5.2l0.1-5.3l-7.7-8l0.4-5.4l-2.3-6.7l6.7-3.4l7.5-6.6l4.7,3.4l2.9-7.6l6.8-2.5L159.5,82.3z"
                    />
                    <text x="155" y="125" className="region-label">
                      봉황면
                    </text>

                    <path
                      id="songwoldong"
                      className={activeRegion === "songwoldong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("songwoldong")}
                      d="M127.3,69.1l0.8,2.5l-2.8,5.8l-6.8-4.2l-3.6,2.2l-4.8-1.7l-0.7-8.9l4.8-3.6L127.3,69.1z"
                    />
                    <text x="118" y="70" className="region-label">
                      송월동
                    </text>

                    <path
                      id="yeonggangdong"
                      className={activeRegion === "yeonggangdong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("yeonggangdong")}
                      d="M114.9,75.4l-6.3,6.3l-5.8,1.6l-7.9-5.2l-2.3,0.7L92,73.3l3.3-6.6l10.3,0.1l3.9-2l0.7,8.9L114.9,75.4z"
                    />
                    <text x="100" y="78" className="region-label">
                      영강동
                    </text>

                    <path
                      id="geumnamdong"
                      className={activeRegion === "geumnamdong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("geumnamdong")}
                      d="M125.1,52.6l2.6,7.4l-0.5,9l-13.1-7.7l-4.8,3.6l-3.9,2l-10.3-0.1L95,53.7l3.3-6.1l5.8-5.2l4.9-1.2
                        l0.9,11.3l4,1.9l2,4.8L125.1,52.6z"
                    />
                    <text x="110" y="60" className="region-label">
                      금남동
                    </text>

                    <path
                      id="seongbukdong3604055"
                      className={activeRegion === "seongbukdong3604055" ? "active-region" : ""}
                      onClick={() => handleRegionClick("seongbukdong3604055")}
                      d="M128.6,48l-3.5,4.6l-9.1,6.6l-2-4.8l-4-1.9l-0.9-11.3l6.5-4.3l8.7-2.3l3.9,3.1L128.6,48z"
                    />
                    <text x="118" y="45" className="region-label">
                      성북동
                    </text>

                    <path
                      id="yeongsandong"
                      className={activeRegion === "yeongsandong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("yeongsandong")}
                      d="M140.1,93.5l-7.5,6.6l-6.7,3.4l-3.5-3.1l-3.2-7.9l-4.3-4l0.7-5.3l-7.1-1.5l6.3-6.3l3.6-2.2l6.8,4.2
                        l2.8-5.8l3.3,8.2l8.7,1.9L140.1,93.5z"
                    />
                    <text x="125" y="88" className="region-label">
                      영산동
                    </text>

                    <path
                      id="ichangdong"
                      className={activeRegion === "ichangdong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("ichangdong")}
                      d="M108.6,81.7l7.1,1.5l-0.7,5.3l4.3,4l3.2,7.9l-7.6,0.2l-4.8-10.4l-8.2,8.9l-6.3,3.3l-10.5-4.7l-4.6,2
                        l-7-1.8l-3-5.3l10,2.3l12.1-16.1l2.3-0.6l7.9,5.2L108.6,81.7z"
                    />
                    <text x="92" y="92" className="region-label">
                      이창동
                    </text>

                    <path
                      id="biccgaramdong"
                      className={activeRegion === "biccgaramdong" ? "active-region" : ""}
                      onClick={() => handleRegionClick("biccgaramdong")}
                      d="M158.2,75.4l-4.3,1.3l-6.6-8.2l3.1-10l4.1-0.8l5.6,0.7l5.2,4.6l1.9,13.8L158.2,75.4z"
                    />
                    <text x="155" y="70" className="region-label">
                      빛가람동
                    </text>
                  </g>
                </g>
              </svg>
              {/* SVG 지도 삽입 끝 */}
            </div>
          </div>
        </div>

        {/* 3열: 오른쪽 - 다시면 정보 + 테이블 + 차트 */}
        <div className="dashboard-column">
          {/* 선택된 지역 상태 */}
          <div className="panel">
            <h3 className="panel-title large">
              전체 기기 평균 ({activeRegion ? `${getRegionName(activeRegion)} 테이블` : "지역 선택"})
              <div className="dashboard-time">{formatTime(currentTime)}</div>
            </h3>
            <div className="status-grid" key={`status-grid-${renderKey}`}>
              <div className="status-card teal">
                <div className="status-number small-font">{(() => {
                  console.log("Rendering deviceCount:", regionalData?.deviceCount, "from regionalData:", regionalData);
                  return regionalData?.deviceCount || 0;
                })()}</div>
                <div className="status-label">기기수</div>
              </div>
              <div className="status-card purple">
                <div className="status-number small-font">{(() => {
                  console.log("Rendering avgH2s:", regionalData?.avgH2s, "from regionalData:", regionalData);
                  return regionalData?.avgH2s?.toFixed(3) || "-";
                })()}</div>
                <div className="status-label">황화수소</div>
              </div>
              <div className="status-card gray">
                <div className="status-number small-font">{(() => {
                  console.log("Rendering avgNh3:", regionalData?.avgNh3, "from regionalData:", regionalData);
                  return regionalData?.avgNh3?.toFixed(3) || "-";
                })()}</div>
                <div className="status-label">암모니아</div>
              </div>
              <div className="status-card orange">
                <div className="status-number small-font">{(() => {
                  console.log("Rendering compositeGrade:", regionalData?.compositeGrade, "from regionalData:", regionalData);
                  return regionalData?.compositeGrade || "-";
                })()}</div>
                <div className="status-label">종합등급</div>
              </div>
            </div>
          </div>

          {/* 테이블 */}
          <div className="panel table-panel" key={`table-${renderKey}`}>
            <div className="button-grid" key={`buttons-${activeDataType}`}>
              {["복합악취", "황화수소", "암모니아", "메탄가스"].map((dataType) => {
                const isActive = activeDataType === dataType;
                console.log(`Button ${dataType}: isActive=${isActive}, activeDataType=${activeDataType}`);
                
                return (
                  <button 
                    key={dataType}
                    className={`control-button ${isActive ? 'active' : ''}`}
                    onClick={() => {
                      console.log("=== Tab clicked ===");
                      console.log("Clicked:", dataType);
                      console.log("Previous active:", activeDataType);
                      setActiveDataType(dataType);
                      setRenderKey(prev => prev + 1); // 강제 리렌더링
                      console.log("New active should be:", dataType);
                    }}
                  >
                    {dataType}
                  </button>
                );
              })}
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>측정값({getUnitByDataType(activeDataType)})</th>
                  <th>등급</th>
                  <th>시간</th>
                </tr>
              </thead>
              <tbody>
                {sensorData.length > 0 ? (
                  sensorData.map((data, index) => (
                    <tr key={index}>
                      <td>{getValueByDataType(data, activeDataType).toFixed(3)}</td>
                      <td>{data.compositeGrade || "-"}</td>
                      <td>
                        {new Date(data.measuredAt).toLocaleString("ko-KR", {
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3}>{loading ? "로딩중..." : "데이터가 없습니다"}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 차트 */}
          <div className="panel last-panel" key={`chart-${renderKey}-${activeDataType}`}>
            <div className="chart-title">
              {activeDataType} ({getUnitByDataType(activeDataType)})
            </div>
            <div className="chart-container">
              <svg className="line-chart" viewBox="0 0 320 160">
                {/* Y축 라벨 */}
                <text x="10" y="15" className="chart-label">
                  1.5
                </text>
                <text x="10" y="50" className="chart-label">
                  1
                </text>
                <text x="10" y="85" className="chart-label">
                  0.5
                </text>
                <text x="10" y="120" className="chart-label">
                  0
                </text>

                {/* 그리드 */}
                <line className="line-chart-grid" x1="30" y1="10" x2="320" y2="10" />
                <line className="line-chart-grid" x1="30" y1="45" x2="320" y2="45" />
                <line className="line-chart-grid" x1="30" y1="80" x2="320" y2="80" />
                <line className="line-chart-grid" x1="30" y1="115" x2="320" y2="115" />

                {/* 데이터 라인 */}
                {chartData.length > 1 && chartData[0].time !== "데이터\n없음" && (() => {
                  const maxValue = Math.max(...chartData.map(item => item.value));
                  const minValue = Math.min(...chartData.map(item => item.value));
                  const range = maxValue - minValue;
                  console.log(`Chart range: ${minValue} - ${maxValue} (range: ${range})`);
                  
                  return (
                    <polyline
                      className="line-chart-path"
                      fill="none"
                      stroke="#00fce3"
                      strokeWidth="2"
                      points={chartData
                        .map((d, i) => {
                          const x = 30 + i * 40;
                          const normalizedValue = range > 0 ? (d.value - minValue) / range : 0.5;
                          const y = 115 - normalizedValue * 100; // 0-100 범위로 정규화
                          return `${x},${Math.max(10, Math.min(115, y))}`;
                        })
                        .join(" ")}
                    />
                  );
                })()}

                {/* 데이터 포인트 */}
                {chartData.map((d, i) => {
                  const maxValue = Math.max(...chartData.map(item => item.value));
                  const minValue = Math.min(...chartData.map(item => item.value));
                  const range = maxValue - minValue;
                  const x = 30 + i * 40;
                  const normalizedValue = range > 0 ? (d.value - minValue) / range : 0.5;
                  const y = 115 - normalizedValue * 100;
                  const clampedY = Math.max(10, Math.min(115, y));
                  
                  return (
                    <g key={`${i}-${activeDataType}-${renderKey}`}>
                      <circle className="line-chart-point" cx={x} cy={clampedY} r="3" fill="#00fce3" />
                      <text x={x} y={clampedY - 8} className="chart-value" textAnchor="middle" fill="#fff" fontSize="10">
                        {d.value.toFixed(3)}
                      </text>
                      <text x={x} y={135} className="chart-time" textAnchor="middle" fill="#ccc" fontSize="10">
                        {d.time.split("\n")[0]}
                      </text>
                      <text x={x} y={145} className="chart-time" textAnchor="middle" fill="#ccc" fontSize="8">
                        {d.time.split("\n")[1]}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
